import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Container,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Paper,
  Divider,
  Fade,
  Slide,
  Grid,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Sidebar, MobileNav } from '@client/components';
import GavelIcon from '@mui/icons-material/Gavel';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';
import PolicyIcon from '@mui/icons-material/Policy';

const PolicyText = `## PyrenzAI Content Removal Policy

At PyrenzAI, we are committed to maintaining a safe, respectful, and legally compliant environment for all users. As such, we reserve the right to review, moderate, and remove any user-generated characters that violate our policies.

## Content Violations

We will delete or restrict access to any characters that include but are not limited to the following:

Pornographic content of any kind  
Child sexual abuse material (CSAM) or any content suggestive of or adjacent to CSAM  
Human-faced characters, whether real, AI-generated, or synthetic (deepfakes, celebrity likenesses, etc.)  
Racist, hateful, or discriminatory characters or content  
Child characters in any context that violates child safety policies  
Content involving:  
Rape or non-consensual acts  
Incest  
Bestiality  
Pedophilia or attraction to minors  
Any other content that promotes or normalizes illegal, violent, or harmful behavior

## Enforcement

Characters found in violation of these rules may be deleted without notice. Repeated or severe violations may result in account warnings, suspensions, or permanent bans.

We take these issues seriously. If you're unsure whether a character is allowed, err on the side of caution or reach out to our support team.

`;

export function ContentPolicy() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showLoginModal, setShowLoginModal] = useState(false);

  return (
    <>
      {isMobile ? (
        <MobileNav setShowLoginModal={setShowLoginModal} />
      ) : (
        <Sidebar />
      )}

      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1a0f0f 0%, #2e1a1a 50%, #3d1515 100%)',
          py: 8,
          px: 2,
        }}
      >
        <Fade in timeout={800}>
          <Container maxWidth="md">
            <Slide direction="up" in timeout={600}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 6 },
                  borderRadius: 4,
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                }}
              >
                <Grid container spacing={3} justifyContent="center">
                  <Grid size={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        mb: 4,
                      }}
                    >
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BlockIcon sx={{ color: 'white', fontSize: 28 }} />
                      </Box>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                          backgroundClip: 'text',
                          textFillColor: 'transparent',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          letterSpacing: '-0.02em',
                        }}
                      >
                        Content Policy
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        mb: 4,
                        color: 'rgba(255, 255, 255, 0.6)',
                      }}
                    >
                      <WarningIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Strictly Enforced Guidelines
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={12}>
                    <Divider
                      sx={{
                        my: 4,
                        borderColor: 'rgba(231, 76, 60, 0.3)',
                      }}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Box
                      sx={{
                        color: 'rgba(255, 255, 255, 0.85)',
                        '& h2': {
                          color: '#e74c3c',
                          fontWeight: 600,
                          fontSize: '1.5rem',
                          mt: 4,
                          mb: 2,
                          pb: 1,
                          borderBottom: '2px solid rgba(231, 76, 60, 0.3)',
                          display: 'inline-block',
                        },
                        '& p': {
                          lineHeight: 1.8,
                          mb: 2,
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                        '& ul': {
                          pl: 0,
                          listStyle: 'none',
                          mb: 3,
                        },
                        '& li': {
                          mb: 1.5,
                          color: 'rgba(255, 255, 255, 0.7)',
                          position: 'relative',
                          pl: 3,
                          '&::before': {
                            content: '"⚠"',
                            position: 'absolute',
                            left: 0,
                            color: '#e74c3c',
                            fontWeight: 'bold',
                          },
                        },
                      }}
                    >
                      <ReactMarkdown
                        components={{
                          h2: ({ node, ...props }) => {
                            const { ref, ...rest } = props;
                            return (
                              <Typography
                                variant="h5"
                                component="h2"
                                gutterBottom
                                {...rest}
                              />
                            );
                          },
                          p: ({ node, ...props }) => {
                            const { ref, ...rest } = props;
                            return (
                              <Typography
                                variant="body1"
                                component="p"
                                paragraph
                                {...rest}
                              />
                            );
                          },
                          ul: ({ node, ...props }) => {
                            const { ref, ...rest } = props;
                            return (
                              <List
                                dense
                                disablePadding
                                {...rest}
                                sx={{
                                  listStyle: 'none',
                                  pl: 0,
                                  mb: 3,
                                }}
                              />
                            );
                          },
                          li: ({ node, children, ...props }) => {
                            const { ref, ...rest } = props;
                            return (
                              <ListItem
                                disablePadding
                                sx={{
                                  mb: 1.5,
                                  position: 'relative',
                                  pl: 3,
                                  '&::before': {
                                    content: '"⚠"',
                                    position: 'absolute',
                                    left: 0,
                                    color: '#e74c3c',
                                    fontWeight: 'bold',
                                  },
                                }}
                                {...rest}
                              >
                                <ListItemText
                                  primary={
                                    <Typography
                                      component="span"
                                      variant="body1"
                                      sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                                    >
                                      {children}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            );
                          },
                        }}
                      >
                        {PolicyText}
                      </ReactMarkdown>
                    </Box>
                  </Grid>

                  <Grid size={12}>
                    <Divider
                      sx={{
                        my: 4,
                        borderColor: 'rgba(231, 76, 60, 0.3)',
                      }}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: 'rgba(255, 255, 255, 0.5)',
                      }}
                    >
                      <GavelIcon fontSize="small" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Violations may result in immediate account termination
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        mt: 2,
                        color: 'rgba(255, 255, 255, 0.5)',
                      }}
                    >
                      <PolicyIcon fontSize="small" />
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Pyrenz AI © 2026. All rights reserved.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Slide>
          </Container>
        </Fade>
      </Box>
    </>
  );
}